import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import PersonalHome from "./pages/PersonalHome";
import AccountingHub from "./pages/AccountingHub";
import ControlRoom from "./pages/ControlRoom";
import NewOffer from "./pages/NewOffer";
import OfferDetail from "./pages/OfferDetail";
import Prezzari from "./pages/Prezzari";
import Cantieri from "./pages/Cantieri";
import SiteDetail from "./pages/SiteDetail";
import Workers from "./pages/Workers";
import Settings from "./pages/Settings";
import Contacts from "./pages/Contacts";
import Customers from "./pages/Customers";
import Suppliers from "./pages/Suppliers";
import EmailInbox from "./pages/EmailInbox";
import WhatsAppInbox from "./pages/WhatsAppInbox";
import CompanyDocuments from "./pages/CompanyDocuments";
import Documents from "./pages/Documents";
import Tenders from "./pages/Tenders";
import Takeoff from "./pages/Takeoff";
import Field from "./pages/Field";
import FieldBackoffice from "./pages/FieldBackoffice";
import AiEngineDashboard from "./pages/AiEngineDashboard";
import Login from "./pages/Login";
import { authApi, getToken, setToken } from "./lib/auth";
import { canMenu } from "./lib/menuPermissions";

const Guard = ({session,section,sub,admin=false,children}) => (!admin || session?.user?.is_admin) && canMenu(session?.user,section,sub) ? children : <div role="alert" className="p-8 text-slate-700"><h2 className="text-xl font-bold">Accesso non autorizzato</h2><p>Questa funzione non è abilitata per il tuo utente.</p></div>;

function App() {
  const backofficeMode = window.location.pathname.startsWith("/field-backoffice");
  const fieldMode = window.location.pathname.startsWith("/field");
  const [authed, setAuthed] = useState(null);
  const [session, setSession] = useState(null);

  const check = useCallback(async () => {
    try {
      const me = await authApi.me();
      setSession(me);
      setAuthed(true);
    } catch {
      setToken(null);
      setSession(null);
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Ellebierre S.R.L. — Gestionale Offerte e Cantieri";
    check();
    const onUnauth = () => setAuthed(false);
    window.addEventListener("edil:unauthorized", onUnauth);
    window.addEventListener("edil:permissions-changed", check);
    window.addEventListener("focus", check);
    return () => {
      window.removeEventListener("edil:unauthorized", onUnauth);
      window.removeEventListener("edil:permissions-changed", check);
      window.removeEventListener("focus", check);
    };
  }, [check]);

  const logout = async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    setToken(null);
    setSession(null);
    setAuthed(false);
  };

  if (backofficeMode) return <BrowserRouter><Toaster position="top-right" richColors closeButton /><Routes><Route path="/field-backoffice/*" element={<FieldBackoffice />} /></Routes></BrowserRouter>;
  if (fieldMode) {
    return (
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes><Route path="/field/*" element={<Field />} /></Routes>
      </BrowserRouter>
    );
  }

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      {!authed ? (
        <Login onSuccess={check} />
      ) : window.location.pathname.startsWith("/ai-engine") ? (
        <Routes><Route path="/ai-engine" element={<Guard session={session} section="offers" sub="ai-engine" admin><AiEngineDashboard /></Guard>} /></Routes>
      ) : (
        <Layout onLogout={logout} session={session}>
          <Routes>
            <Route path="/control-room" element={<Guard session={session} section="control-room"><ControlRoom /></Guard>} />
            <Route path="/fornitori" element={<Guard session={session} section="suppliers"><Suppliers /></Guard>} />
            <Route path="/contabilita-cantieri" element={<Guard session={session} section="accounting"><AccountingHub session={session} /></Guard>} />
            <Route path="/" element={<PersonalHome session={session} />} />
            <Route path="/offerte" element={<Guard session={session} section="offers" sub="list"><Dashboard /></Guard>} />
            <Route path="/nuova-offerta" element={<Guard session={session} section="offers"><NewOffer /></Guard>} />
            <Route path="/offerte/:id" element={<Guard session={session} section="offers"><OfferDetail session={session} /></Guard>} />
            <Route path="/prezzari" element={<Guard session={session} section="pricebooks"><Prezzari session={session} /></Guard>} />
            <Route path="/gare" element={<Guard session={session} section="tenders"><Tenders /></Guard>} />
            <Route path="/takeoff" element={<Guard session={session} section="offers"><Takeoff /></Guard>} />
            <Route path="/cantieri" element={<Guard session={session} section="sites"><Cantieri /></Guard>} />
            <Route path="/cantieri/nuovo" element={<Guard session={session} section="sites"><Cantieri /></Guard>} />
            <Route path="/cantieri/:id" element={<Guard session={session} section="sites"><SiteDetail session={session} /></Guard>} />
            <Route path="/operai" element={<Guard session={session} section="workers"><Workers session={session} /></Guard>} />
            <Route path="/clienti" element={<Guard session={session} section="customers"><Customers /></Guard>} />
            <Route path="/contatti" element={<Guard session={session} section="contacts"><Contacts /></Guard>} />
            <Route path="/email" element={<Guard session={session} section="emails"><EmailInbox /></Guard>} />
            <Route path="/whatsapp" element={<Guard session={session} section="whatsapp"><WhatsAppInbox /></Guard>} />
            <Route path="/documenti" element={<Guard session={session} section="documents"><Documents session={session} /></Guard>} />
            <Route path="/documenti-societa" element={<Guard session={session} section="documents"><CompanyDocuments /></Guard>} />
            <Route path="/impostazioni" element={<Guard session={session} section="settings"><Settings session={session} /></Guard>} />
          </Routes>
        </Layout>
      )}
    </BrowserRouter>
  );
}

export default App;
